// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import ReactPixel from 'react-facebook-pixel';

// Auth
import { AuthProvider } from './context/AuthContext';

// Components
import LandingPage from './components/LandingPage';
import LinkJourneyCode from './components/LinkJourneyCode';
import PasswordReset from './components/PasswordReset';
import JourneyPage from './components/JourneyPage';
import PaymentPage from './PaymentPage';
import PaymentCancelled from './PaymentCancelled';
import AddPhone from './components/AddPhone';
import InauguralPassSignup from './components/InauguralPassSignup';
import InauguralConfirmationPage from './components/InauguralConfirmationPage';
import OriginStory from './components/OriginStory';
import BackThePass from './components/BackThePass';
import LearnMore from './components/LearnMore';
import HayleeDinner from './components/HayleeDinner';
import CreatorLanding from './components/CreatorLanding';
import CreatorWelcome from './components/CreatorWelcome';
import CreatorSpecialOffer from './components/CreatorSpecialOffer';
import CreatorInfoPage from './components/CreatorInfoPage';
import CreatorSubmissionForm from './components/CreatorSubmissionForm';
import CreatorRoute from './components/CreatorRoute';
import CreatorDashboard from './components/CreatorDashboard';
import RoutePerformance from './components/RoutePerformance';
import RoleSelection from './components/RoleSelection';
import MobileRedirect from './components/MobileRedirect';

// Admin route guard
import AdminRoute from './admin/AdminRoute';
import AdminLayout from './admin/AdminLayout';
import AdminBookingsPage from './admin/AdminBookingsPage';
import AdminJourneysPage from './admin/AdminJourneysPage';
import AdminHostelsPage from './admin/AdminHostelsPage';
import AdminCitiesPage from './admin/AdminCitiesPage';
import AdminUsersPage from './admin/AdminUsersPage';
import AdminTipsPage from './admin/AdminTipsPage';
import AdminUserPermissions from './admin/AdminUserPermissions';
import AdminExploreCardsPage from './admin/AdminExploreCardsPage';
import AdminRoutesPage from './admin/AdminRoutesPage';
import AdminRouteRevisionsPage from './admin/AdminRouteRevisionsPage';
import AdminTermsPage from './admin/AdminTermsPage';
import AdminPrivacyPage from './admin/AdminPrivacyPage';
import AdminExchangeRates from './admin/AdminExchangeRates';
import AdminPricingCalendar from './admin/AdminPricingCalendar';
import AdminTransactions from './admin/AdminTransactionsPage';
import AdminSampleData from './admin/AdminSampleData';

// Other user-facing pages
import UserInfoForm from './UserInfoForm';
import GlobePage from './components/GlobePage';
import NotFoundPage from './404';
import UnifiedSignIn from './components/UnifiedSignIn';
import UserDashboard from './components/UserDashboard';
import StorePage from './store/StorePage';
import MemoriesPage from './components/MemoriesPage';
import DeleteAccount from './components/DeleteAccount';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';

// Optional: If you want to test phone auth with the new approach
import PhoneAuthTest from './components/PhoneAuthTest';

import './App.css';

console.log('BackThePass:', BackThePass);
console.log('LearnMore:', LearnMore);
console.log('Type of BackThePass:', typeof BackThePass);
console.log('Type of LearnMore:', typeof LearnMore);

// Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isLearnMorePage = location.pathname === '/learn-more';
  const isOriginStoryPage = location.pathname === '/origin-story';
  const isBackThePassPage = location.pathname === '/back-the-pass';

  return (
    <div className={`App ${isLandingPage ? 'landing-page-active' : ''} ${isLearnMorePage ? 'learn-more-active' : ''} ${isOriginStoryPage ? 'origin-story-active' : ''} ${isBackThePassPage ? 'back-the-pass-active' : ''}`}>
      <div className="main-content">
        <Routes>
          {/* Root path: Now shows the LandingPage component */}
          <Route path="/" element={<LandingPage />} />

          <Route path="/back-the-pass" element={<BackThePass />} />
          <Route path="/learn-more" element={<LearnMore />} />
          <Route path="/creators" element={<CreatorLanding />} />
          <Route path="/creator-welcome" element={<CreatorWelcome />} />
          <Route path="/creator-special/gemma" element={<CreatorSpecialOffer />} />
          <Route path="/creator-info" element={<CreatorInfoPage />} />

          {/* Protected Creator Routes */}
          <Route path="/creator-dashboard" element={
            <CreatorRoute>
              <CreatorDashboard />
            </CreatorRoute>
          } />
          <Route path="/creator-submit" element={
            <CreatorRoute>
              <CreatorSubmissionForm />
            </CreatorRoute>
          } />
          <Route path="/creator-submit/:routeId" element={
            <CreatorRoute>
              <CreatorSubmissionForm />
            </CreatorRoute>
          } />
          <Route path="/route-performance/:routeId" element={
            <CreatorRoute>
              <RoutePerformance />
            </CreatorRoute>
          } />

          {/* Origin Story Page */}
          <Route path="/origin-story" element={<OriginStory />} />

          {/* Haylee Dinner Page */}
          <Route path="/haylee" element={<HayleeDinner />} />

          {/* Privacy Policy Page */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          
          {/* Terms and Conditions Page */}
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* Login redirects to unified signin */}
          <Route path="/login" element={<Navigate to="/signin" replace />} />

          {/* Link Journey Code */}
          <Route path="/link-code" element={<LinkJourneyCode />} />

          {/* Unified Sign In */}
          <Route path="/signin" element={<UnifiedSignIn />} />

          {/* Role Selection for Admin+Creator users */}
          <Route path="/role-selection" element={<RoleSelection />} />

          {/* Mobile Redirect for regular users */}
          <Route path="/mobile-redirect" element={<MobileRedirect />} />

          {/* Optional Phone Auth Test route */}
          <Route path="/phone-auth-test" element={<PhoneAuthTest />} />

          {/* Admin Login Page - Redirect to unified signin */}
          <Route path="/admin-login" element={<Navigate to="/signin" replace />} />

          {/* Add Phone Page */}
          <Route path="/add-phone" element={<AddPhone />} />

          {/* Inaugural Pass Signup */}
          <Route path="/inaugural-signup" element={<InauguralPassSignup />} />
          
          {/* Inaugural Pass Confirmation */}
          <Route path="/inaugural-confirmation" element={<InauguralConfirmationPage />} />

          {/* Param-based route for Journey */}
          <Route path="/journey/:journeyId" element={<JourneyPage />} />

          {/* Payment-related routes */}
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />

          {/* Protected admin routes: nest under /admin */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            {/* Index route -> /admin => /admin/journeys */}
            <Route index element={<AdminBookingsPage />} />
            <Route path="bookings" element={<AdminBookingsPage />} />
            <Route path="journeys" element={<AdminJourneysPage />} />
            <Route path="routes" element={<AdminRoutesPage />} />
            <Route path="revisions" element={<AdminRouteRevisionsPage />} />
            <Route path="hostels"  element={<AdminHostelsPage />} />
            <Route path="cities"   element={<AdminCitiesPage />} />
            <Route path="users"    element={<AdminUsersPage />} />
            <Route path="tips"     element={<AdminTipsPage />} />
            <Route path="terms" element={<AdminTermsPage />} />
            <Route path="privacy" element={<AdminPrivacyPage />} />
            <Route path="permissions" element={<AdminUserPermissions />} />
            <Route path="explore-cards" element={<AdminExploreCardsPage />} />
            <Route path="exchange-rates" element={<AdminExchangeRates />} />
            <Route path="pricing-calendar" element={<AdminPricingCalendar />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="sample-data" element={<AdminSampleData />} />
          </Route>

          {/* User info form */}
          <Route path="/user-info" element={<UserInfoForm />} />

          {/* GlobePage */}
          <Route path="/globe" element={<GlobePage />} />

          {/* User Dashboard */}
          <Route path="/dashboard" element={<UserDashboard />} />

          {/* Password Reset */}
          <Route path="/password-reset" element={<PasswordReset />} />

          {/* Store Page */}
          <Route path="/store" element={<Navigate to="/go" replace />} />
          <Route path="/go" element={<StorePage />} />

          {/* Memories/Achievements Page */}
          <Route path="/memories" element={<MemoriesPage />} />

          {/* Delete Account Page */}
          <Route path="/delete-account" element={<DeleteAccount />} />

          {/* Catch-all route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  // Initialize Facebook Pixel
  useEffect(() => {
    const pixelId = '1775079993142805';
    const options = {
      autoConfig: true, // Automatically track PageView on route changes
      debug: process.env.NODE_ENV === 'development', // Enable debug mode in development
    };

    ReactPixel.init(pixelId, undefined, options);
    ReactPixel.pageView(); // Track initial page view

    console.log('✅ Facebook Pixel initialized:', pixelId);
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <Router future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </Elements>
  );
}

export default App;